import _jsx from '@babel/runtime/helpers/esm/jsx';
import _forIn from 'lodash/forIn';
import _debounce from 'lodash/debounce';
import _mapValues from 'lodash/mapValues';
import { createContext, useState, useRef, useCallback, useEffect, useContext } from 'react';
import { breakpoints } from '@dbh/breakpoints';
import { useMakeSelect } from '@dbh/reselect-extra';
import { makeSelectComputedSSrBreakpointsForRendering } from '@dbh/ssr-data-redux';
import { addEventListenerToMediaQueryList, removeEventListenerFromMediaQueryList } from '@dbh/media-queries';
import throwInServerSideRendering from '@dbh/throw-in-server-side-rendering';
import 'prop-types';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';

// defined in `./hooks`.
const BaseContextBreakPoints=/*#__PURE__*/createContext(null);const MEDIA_QUERY_BATCH_DELAY=250;

const mediaQueries=_mapValues(breakpoints,a=>"(min-width: "+a+"px)"),{Provider}=BaseContextBreakPoints,BreakpointsProvider=a=>{let{children:b}=a;const c=useMakeSelect(makeSelectComputedSSrBreakpointsForRendering),d=c.get("breakpointsMap"),e=c.get("breakpoint"),f={...d.toObject(),breakpointUsedToRenderSsrHtml:e},[g,h]=useState(f),i=useRef(),j=useRef(_debounce(()=>{const a=_mapValues(i.current,a=>{let{matches:b}=a;return b});a.breakpointUsedToRenderSsrHtml=e,h(a);},MEDIA_QUERY_BATCH_DELAY)),k=useCallback(()=>{throwInServerSideRendering("addEventListeners"),_forIn(i.current,a=>{addEventListenerToMediaQueryList(a,j.current);});},[i,j]),l=useCallback(()=>{throwInServerSideRendering("removeEventListeners"),_forIn(i.current,a=>{removeEventListenerFromMediaQueryList(a,j.current);});},[i]),m=useCallback(()=>(throwInServerSideRendering("createMediaQueryMatchers"),_mapValues(mediaQueries,window.matchMedia)),[]),n=useCallback(()=>{i.current=m();},[m]),o=useCallback(()=>{i.current&&(l(i.current),i.current=null);},[i,l]);return useEffect(()=>(n(),()=>{o();}),[n,o]),useEffect(()=>{k();},[k,i]),useEffect(()=>{i.current&&j.current();},[i]),/*#__PURE__*/_jsx(Provider,{value:g},void 0,b)};BreakpointsProvider.displayName="BreakpointsProvider";var BreakpointsProvider$1 = BreakpointsProvider;

/**
 * Custom `react` hook that returns the value of the breakpoints context.
 * We created this to be able to mock it in the `jest` tests, mocking the
 * context directly would not be as easy.
 * @return {Object?} .
 */const useBreakpointsContext=()=>useContext(BaseContextBreakPoints);var useBreakpointsContext$1 = useBreakpointsContext;

/**
 * Custom `react` hook that returns whether or not the current viewport is
 * greater or equal the input breakpoint.
 * Note: `xs` is **not** a valid breakpoint key.
 * @param {string} breakpointKey .
 * @return {boolean} `true` if the viewport is greater or equal the breakpoint.
 */const useIsGreaterOrEqualBreakpoint=withConformsTo("useIsGreaterOrEqualBreakpoint",[],a=>{const b=useBreakpointsContext$1(),c=b[a],[d,e]=useState(c);return useEffect(()=>{e(c);},[c]),d});var useIsGreaterOrEqualBreakpoint$1 = useIsGreaterOrEqualBreakpoint;

/**
 * Custom `react` hook that returns whether or not the current breakpoint is
 * considered a `mobile` breakpoint.
 * @return {boolean?} `true` if the current breakpoint is considered `mobile`.
 * @see {@link https://reactjs.org/docs/hooks-custom.html}
 */const useIsMobileBreakpoint=()=>!useIsGreaterOrEqualBreakpoint$1("lg");var useIsMobileBreakpoint$1 = useIsMobileBreakpoint;

/**
 * Custom `react` hook that returns the input value that matches the current
 * breakpoint.
 * @param {Object} values .
 * @param {number} values.xs .
 * @param {number} values.sm .
 * @param {number} values.md .
 * @return {number} .
 */const useValuesForBreakpoints=withConformsTo("useValuesForBreakpoints",[],a=>{const{xs:b,sm:c,md:d}=a,e=useBreakpointsContext$1(),{sm:f,md:g}=e;return g?d:f?c:b});var useValuesForBreakpoints$1 = useValuesForBreakpoints;

/**
 * Detects if the current window’s breakpoint is less than breakpoint.
 * @param {number} breakpoint .
 * @return {boolean} .
 */const isBreakpointLessThan=withConformsTo("isBreakpointLessThan",[],a=>(throwInServerSideRendering("isBreakpointLessThan"),window.matchMedia("(max-width: "+a+"px)").matches));/**
 * Use this only when you need the breakpoint in a saga. Otherwise use
 * the hooks in `./hooks`.
 * @return {boolean} `true` if the current breakpoint is considered mobile.
 */const isCurrentBreakpointMobileDoNotUseInComponents=()=>{const{lg:a}=breakpoints;return isBreakpointLessThan(a)};

export { BaseContextBreakPoints, BreakpointsProvider$1 as BreakpointsProvider, isCurrentBreakpointMobileDoNotUseInComponents, useBreakpointsContext$1 as useBreakpointsContext, useIsGreaterOrEqualBreakpoint$1 as useIsGreaterOrEqualBreakpoint, useIsMobileBreakpoint$1 as useIsMobileBreakpoint, useValuesForBreakpoints$1 as useValuesForBreakpoints };
